/**
 * A same-origin link that is a real link.
 *
 * This surface is indexable, so every navigation a crawler needs is an anchor
 * with a real `href` that resolves server-side. The click handler upgrades it
 * to a client-side navigation, and only when the browser would otherwise
 * perform a plain left-click navigation — a modified click, a middle click, or
 * a right-click context menu keeps the browser's own behaviour, because
 * "open in a new tab" is how people compare two slabs.
 */

import type { MouseEvent, ReactNode } from "react";
import { useStorefront } from "../app/StorefrontContext.js";

export function InternalLink({
  href,
  children,
  className,
  current,
  ariaLabel,
  onNavigate
}: {
  href: string;
  children: ReactNode;
  className?: string;
  current?: boolean;
  ariaLabel?: string;
  onNavigate?: () => void;
}): ReactNode {
  const { navigate } = useStorefront();

  const onClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(href);
    onNavigate?.();
  };

  return (
    <a
      aria-label={ariaLabel}
      {...(className === undefined ? {} : { className })}
      {...(current === true ? { "aria-current": "page" as const } : {})}
      href={href}
      onClick={onClick}
    >
      {children}
    </a>
  );
}
